import { pluralizeUk } from '@/Ship/Helpers/pluralize'

export type DeltaTone = 'good' | 'bad' | 'neutral'

export interface Delta {
    /** Текст зміни: «+12%», «−3», «+1,5 п.п.»; null — порівнювати нема з чим */
    text: string | null
    tone: DeltaTone
    direction: 'up' | 'down' | 'flat'
}

const format = (value: number, digits: number): string =>
    Math.abs(value).toLocaleString('uk-UA', { maximumFractionDigits: digits, minimumFractionDigits: 0 })

const signed = (value: number, digits: number, suffix = ''): string =>
    `${value > 0 ? '+' : value < 0 ? '−' : ''}${format(value, digits)}${suffix}`

const toneOf = (diff: number, higherIsBetter: boolean): DeltaTone => {
    if (diff === 0) return 'neutral'
    return diff > 0 === higherIsBetter ? 'good' : 'bad'
}

const directionOf = (diff: number): Delta['direction'] => (diff > 0 ? 'up' : diff < 0 ? 'down' : 'flat')

/**
 * Зміна кількісного показника до попереднього відрізку у відсотках. Якщо раніше було 0 —
 * абсолютна зміна («+3»; для тонн — з `digits` знаками після коми: «+0,3»): відсоток від нуля
 * не має сенсу. Обидва нулі — зміни немає.
 */
export const countDelta = (current: number, previous: number, higherIsBetter = true, digits = 0): Delta => {
    const diff = current - previous
    if (current === 0 && previous === 0) return { text: null, tone: 'neutral', direction: 'flat' }
    if (previous === 0)
        return { text: signed(diff, digits), tone: toneOf(diff, higherIsBetter), direction: directionOf(diff) }
    const percent = Math.round((diff / previous) * 100)
    return { text: signed(percent, 0, '%'), tone: toneOf(percent, higherIsBetter), direction: directionOf(percent) }
}

/**
 * Зміна показника-частки чи середнього — абсолютна різниця (в. п. / бали), бо відсоток
 * від відсотка плутає. null з будь-якого боку — порівнювати нема з чим.
 */
export const valueDelta = (
    current: number | null,
    previous: number | null,
    {
        higherIsBetter = true,
        digits = 1,
        suffix = '',
    }: { higherIsBetter?: boolean; digits?: number; suffix?: string } = {}
): Delta => {
    if (current === null || previous === null) return { text: null, tone: 'neutral', direction: 'flat' }
    const factor = 10 ** digits
    const diff = Math.round((current - previous) * factor) / factor
    return { text: signed(diff, digits, suffix), tone: toneOf(diff, higherIsBetter), direction: directionOf(diff) }
}

/** Частка задач із затримкою, % (одна цифра після коми); null — задач немає */
export const delayedShare = (delayed: number, tasks: number): number | null =>
    tasks > 0 ? Math.round((delayed / tasks) * 1000) / 10 : null

/** Скільки триває затримка: «12 хв», «2 год 15 хв», «3 д 4 год» */
export const formatDuration = (fromIso: string | null, now: Date = new Date()): string | null => {
    if (!fromIso) return null
    const from = new Date(fromIso).getTime()
    if (Number.isNaN(from)) return null
    const minutes = Math.max(0, Math.floor((now.getTime() - from) / 60000))
    if (minutes < 60) return `${minutes} хв`
    const hours = Math.floor(minutes / 60)
    if (hours < 24) return minutes % 60 ? `${hours} год ${minutes % 60} хв` : `${hours} год`
    const days = Math.floor(hours / 24)
    return hours % 24 ? `${days} д ${hours % 24} год` : `${days} д`
}

const pad2 = (value: number): string => String(value).padStart(2, '0')
const dayKey = (date: Date): string => `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`

/**
 * Коли отримано затримку, коротко: «сьогодні 14:32», «вчора 08:10», «29.09 08:10»,
 * інший рік — «07.07.2025 06:14». Повна дата з секундами в списку лише шумить.
 */
export const formatReceivedAt = (iso: string | null, now: Date = new Date()): string | null => {
    if (!iso) return null
    const date = new Date(iso)
    if (Number.isNaN(date.getTime())) return null
    const time = `${pad2(date.getHours())}:${pad2(date.getMinutes())}`
    const yesterday = new Date(now)
    yesterday.setDate(now.getDate() - 1)
    if (dayKey(date) === dayKey(now)) return `сьогодні ${time}`
    if (dayKey(date) === dayKey(yesterday)) return `вчора ${time}`
    const dayMonth = `${pad2(date.getDate())}.${pad2(date.getMonth() + 1)}`
    return date.getFullYear() === now.getFullYear()
        ? `${dayMonth} ${time}`
        : `${dayMonth}.${date.getFullYear()} ${time}`
}

/**
 * Які дати підписувати на осі динаміки: понеділки й сьогодні. Понеділок за 1–2 дні до
 * сьогодні пропускається, інакше підписи злипаються («28.09» упритул до «30.09»).
 */
export const axisLabelDates = (dates: string[], today: string): Set<string> => {
    const todayIndex = dates.indexOf(today)
    const result = new Set<string>()
    dates.forEach((date, index) => {
        const isMonday = new Date(`${date}T00:00:00`).getDay() === 1
        const nearToday = todayIndex >= 0 && index !== todayIndex && Math.abs(index - todayIndex) <= 2
        if (date === today || (isMonday && !nearToday)) result.add(date)
    })
    return result
}

/** «5 задач» */
export const tasksWord = (count: number): string => `${count} ${pluralizeUk(count, ['задача', 'задачі', 'задач'])}`
